// src/components/module/almacen/InventariosModal/InventariosModal.tsx
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon, Save, Warehouse, Search as SearchIcon } from "@mui/icons-material";
import { InventarioDto } from "../../../../services/api/inventariosApi";
import { useArticulosApi, ArticuloSearchResult } from "../../../../services/api/articulosApi";
import { useSucursalesApi } from "../../../../services/api/sucursalesApi";
import { DarkModeContext } from "../../../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", hover: "rgba(255,255,255,0.04)" },
};

const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);

interface Props {
  isOpen: boolean; onClose: () => void; onSubmit: (data: InventarioDto) => Promise<void>;
  title: string; initialData?: InventarioDto; isLoading: boolean;
}

const InventariosModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, title, initialData, isLoading }) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [formData, setFormData] = useState<InventarioDto>({ IdArticulo: 0, IdSucursal: 0, stock: 0, StockMinimo: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ArticuloSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { searchArticulos } = useArticulosApi();
  const { getAll: getSucursales } = useSucursalesApi();
  const [sucursales, setSucursales] = useState<any[]>([]);

  // Load sucursales once
  useEffect(() => {
    getSucursales().then((s: any[]) => setSucursales(s.filter((x: any) => x.Activo)));
  }, []);

  // Reset form and set focus when modal opens
  useEffect(() => {
    setFormData(initialData || { IdArticulo: 0, IdSucursal: 0, stock: 0, StockMinimo: 0 });
    setSearchTerm("");
    setSearchResults([]);
    if (isOpen) {
      // Defer focus until Dialog transition completes
      setTimeout(() => searchInputRef.current?.focus(), 120);
    }
  }, [isOpen, initialData]);

  // Reactive search with debounce
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchArticulos(searchTerm);
        setSearchResults(results);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); await onSubmit(formData); };
  const set = (f: string, v: any) => setFormData({ ...formData, [f]: v });
  const inputSx = { "& .MuiInputBase-root": { bgcolor: p.bgS } };

  const handleSelectArticulo = (id: number) => {
    set("IdArticulo", id);
    const found = searchResults.find(a => a.Id === id);
    if (found) setSearchTerm(found.Nombre);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}><Warehouse sx={{ fontSize: 20 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>{title}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>Asignación de stock por sucursal</Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            <Grid container spacing={2.5}>
              {/* Buscador reactivo */}
              <Grid item xs={12}>
                <FieldLabel label="Buscar Artículo" color={p.textT} />
                <TextField
                  size="small" fullWidth value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); set("IdArticulo", 0); }}
                  placeholder="Escriba nombre o EAN (mín. 2 caracteres)..."
                  inputRef={searchInputRef}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{searching ? <CircularProgress size={16} /> : <SearchIcon sx={{ fontSize: 18, color: p.textT }} />}</InputAdornment>,
                  }}
                  sx={inputSx}
                />
              </Grid>

              {/* Selector de artículo */}
              <Grid item xs={12}>
                <FieldLabel label="Artículo" required color={p.textT} />
                <TextField
                  select size="small" fullWidth value={formData.IdArticulo}
                  onChange={(e) => handleSelectArticulo(Number(e.target.value))}
                  sx={inputSx}
                  disabled={searchResults.length === 0 && !initialData}
                  helperText={
                    !initialData && searchResults.length === 0 && searchTerm.length < 2
                      ? "Use el buscador para filtrar artículos"
                      : !initialData && searchResults.length === 0 && searchTerm.length >= 2
                      ? "Sin resultados para la búsqueda"
                      : ""
                  }
                >
                  <MenuItem value={0} disabled>Seleccione un artículo</MenuItem>
                  {/* Si estamos editando, mostrar el artículo actual aunque no esté en results */}
                  {initialData && formData.IdArticulo > 0 && !searchResults.find(r => r.Id === formData.IdArticulo) && (
                    <MenuItem value={formData.IdArticulo}>
                      <Typography sx={{ fontSize: "0.84rem", color: p.textT }}>Artículo actual (ID: {formData.IdArticulo})</Typography>
                    </MenuItem>
                  )}
                  {searchResults.map(a => (
                    <MenuItem key={a.Id} value={a.Id}>
                      <Typography sx={{ fontSize: "0.84rem" }}>
                        {a.Nombre} — <Box component="span" sx={{ fontFamily: "'IBM Plex Mono', monospace", color: p.textT }}>{a.EAN}</Box>
                      </Typography>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <FieldLabel label="Sucursal" required color={p.textT} />
                <TextField select size="small" fullWidth value={formData.IdSucursal} onChange={(e) => set("IdSucursal", Number(e.target.value))} sx={inputSx}>
                  <MenuItem value={0} disabled>Seleccione una sucursal</MenuItem>
                  {sucursales.map(s => <MenuItem key={s.Id} value={s.Id}>{s.Nombre}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <FieldLabel label="Stock Actual" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.stock} onChange={(e) => set("stock", Number(e.target.value))} onFocus={(e) => e.target.select()} inputProps={{ min: 0 }} sx={{ ...inputSx, "& .MuiInputBase-input": { fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 } }} />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel label="Stock Mínimo" required color={p.textT} />
                <TextField type="number" size="small" fullWidth value={formData.StockMinimo} onChange={(e) => set("StockMinimo", Number(e.target.value))} onFocus={(e) => e.target.select()} inputProps={{ min: 0 }} sx={{ ...inputSx, "& .MuiInputBase-input": { fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 } }} />
              </Grid>
            </Grid>
          </DialogContent>

          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button onClick={onClose} disabled={isLoading} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>Cancelar</Button>
            <Button type="submit" variant="contained" disableElevation disabled={isLoading || !formData.IdArticulo || !formData.IdSucursal}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
              {isLoading ? "Guardando..." : initialData ? "Actualizar" : "Guardar"}
            </Button>
          </Box>
        </form>
      </Dialog>
  );
};

export default InventariosModal;
